import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { brand } from "../mock";

const LegalPage = ({ title, updated, sections }) => (
  <div className="min-h-screen bg-[#F5F7FB] dark:bg-[#08132C]">
    <header className="border-b border-slate-200 dark:border-white/10 bg-white/90 dark:bg-[#08132C]/95 backdrop-blur">
      <div className="container-x max-w-5xl py-6">
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-[#0B1B3B] dark:text-white hover:text-[#1E56E3]">
          <ArrowLeft size={16} /> Back to site
        </Link>
        <div className="mt-8">
          <div className="text-xs font-semibold uppercase tracking-[0.28em] text-[#1E56E3]">{brand.legal}</div>
          <h1 className="mt-3 text-4xl sm:text-5xl font-extrabold text-[#0B1B3B] dark:text-white">{title}</h1>
          <p className="mt-4 text-sm text-slate-500 dark:text-slate-300">Last updated {updated}</p>
        </div>
      </div>
    </header>

    <main className="container-x max-w-5xl py-14">
      <article className="rounded-[32px] border border-slate-200 dark:border-white/10 bg-white dark:bg-[#0B1B3B] p-8 md:p-12 shadow-[0_24px_80px_-40px_rgba(11,27,59,0.25)]">
        {sections.map((section) => (
          <section key={section.h} className="mb-10 last:mb-0">
            <h2 className="text-xl md:text-2xl font-bold text-[#0B1B3B] dark:text-white">{section.h}</h2>
            {(section.p || []).map((paragraph) => (
              <p key={`${section.h}-${paragraph.slice(0, 24)}`} className="mt-3 text-[15px] text-slate-600 dark:text-slate-300 leading-relaxed">
                {paragraph}
              </p>
            ))}
            {section.list && (
              <ul className="mt-4 space-y-3">
                {section.list.map((item) => (
                  <li key={`${section.h}-${item.slice(0, 28)}`} className="flex items-start gap-3 text-[14px] text-slate-700 dark:text-slate-200 leading-relaxed">
                    <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#1E56E3] shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}

        <div className="mt-12 rounded-2xl border border-slate-200 dark:border-white/10 bg-[#F8FAFF] dark:bg-white/5 p-5 text-sm text-slate-600 dark:text-slate-300">
          For privacy or legal questions, contact <a href={`mailto:${brand.email}`} className="font-semibold text-[#1E56E3]">{brand.email}</a> or use the <Link to="/contact" className="font-semibold text-[#1E56E3] inline-flex items-center gap-1">contact page <ArrowUpRight size={14} /></Link>.
        </div>
      </article>
    </main>
  </div>
);

export const PrivacyPolicy = () => (
  <LegalPage
    title="Privacy Policy"
    updated="August 2026"
    sections={[
      { h: "Privacy Notice", p: [
        "This Privacy Policy explains how V4 Data And AI Solutions (“V4”, “we”, “our”) collects, uses, stores, and protects personal information submitted through our website and business enquiry channels.",
        "It applies when you browse this website, submit a contact request, book an appointment, request information about our services, or interact with us for business-related communication.",
      ]},
      { h: "Information We Collect", p: [
        "We collect the information you choose to provide, including your name, work email, organization, optional phone number, message, and appointment-related preferences.",
        "We may also collect technical information such as browser type, device details, pages visited, and traffic patterns to improve website stability and usability.",
      ]},
      { h: "How We Use Your Information", list: [
        "To respond to enquiries, project discussions, and consultation requests.",
        "To schedule calls or follow-up conversations that you request through this website.",
        "To improve our website content, user experience, and service messaging.",
        "To maintain internal records, comply with legal obligations, and protect our business operations.",
      ]},
      { h: "Cookies and Tracking Technologies", p: [
        "Like modern business websites, we may use essential cookies or browser storage to remember functional preferences, protect admin sessions, and support core site behavior.",
        "Where analytics or performance technologies are used in the future, they will be applied only to understand aggregated site usage and improve visitor experience.",
      ]},
      { h: "When We Share Information", p: [
        "We do not sell your personal information. We may share information only with trusted service providers, hosting partners, or legal authorities where necessary to operate the website, respond to requests, or comply with applicable law.",
      ]},
      { h: "Data Retention and Security", p: [
        "Enquiry details are stored in secure systems with controlled access and are retained only as long as needed for business follow-up, record-keeping, or legal compliance.",
        "We use reasonable technical and organizational safeguards to protect your information against unauthorized access, misuse, or disclosure.",
      ]},
      { h: "Your Rights", list: [
        "Request access to the personal information you have submitted to us.",
        "Ask us to update, correct, or delete your information where permitted by law.",
        "Withdraw consent for future communications at any time.",
      ]},
      { h: "Contact and Updates", p: [
        "If you have questions about this Privacy Policy or how your information is handled, please contact V4 Data And AI Solutions using the email address shown on this page.",
        "We may update this Privacy Policy from time to time. The latest revision date will always be shown at the top of this page.",
      ]},
    ]}
  />
);

export const CookiePolicy = () => (
  <LegalPage
    title="Cookie Policy"
    updated="August 2026"
    sections={[
      { h: "Overview", p: [
        "This Cookie Policy explains how V4 Data And AI Solutions uses cookies and similar technologies on this website. It follows the structured approach commonly used by enterprise analytics firms: clear categories, purpose-based disclosure, and user control.",
      ]},
      { h: "What Are Cookies?", p: [
        "Cookies are small text files stored on your device when you visit a website. They help websites remember your preferences, protect sessions, and understand how pages are used.",
      ]},
      { h: "Cookie Categories", list: [
        "Strictly Necessary: required for core website functionality such as secure admin session handling and navigation support.",
        "Functional: used to remember user preferences such as visual theme choices or interaction settings.",
        "Analytics and Performance: may be used to understand how visitors use the site so we can improve content, usability, and performance.",
        "Marketing: V4 Data And AI Solutions does not currently rely on advertising cookies to profile visitors across the web.",
      ]},
      { h: "How We Use Cookies", p: [
        "At present, this website uses limited functional browser storage and secure cookies to support theme preferences and protected admin access. We do not use invasive cross-site tracking technologies on public visitor pages.",
      ]},
      { h: "Third-Party Services", p: [
        "If third-party analytics, embedded media, or scheduling services are introduced, they may place their own cookies subject to their separate privacy and cookie practices.",
      ]},
      { h: "Managing Preferences", list: [
        "You can clear or block cookies from your browser settings at any time.",
        "Disabling essential cookies may affect admin access or some website preferences.",
        "If a consent tool is added in the future, you will be able to manage cookie categories directly on the site.",
      ]},
    ]}
  />
);

export const Terms = () => (
  <LegalPage
    title="Terms of Use"
    updated="August 2026"
    sections={[
      { h: "Acceptance of Terms", p: [
        "These Terms of Use govern access to and use of the V4 Data And AI Solutions website. By using this website, you agree to these Terms. If you do not agree, you should not use the site.",
      ]},
      { h: "Use of the Website", p: [
        "This site is provided for informational and business-evaluation purposes. You may view its content, submit enquiries, and use it to learn about our services, expertise, and case studies.",
      ]},
      { h: "Permitted Use", list: [
        "You may browse and reference the website for legitimate personal or business purposes.",
        "You may not copy, republish, resell, scrape, or misuse the website content without prior written permission.",
        "You may not interfere with the operation, security, or availability of the website.",
      ]},
      { h: "Intellectual Property", p: [
        "All website content, including text, design, layout, branding, graphics, code, and downloadable resources, belongs to V4 Data And AI Solutions unless otherwise stated and is protected by applicable intellectual property laws.",
      ]},
      { h: "Submissions", p: [
        "If you submit information through a contact form, enquiry flow, appointment request, or other input field, you confirm that the details are accurate and that you have the right to share them.",
      ]},
      { h: "Third-Party Links", p: [
        "This website may include links to third-party websites or tools. V4 Data And AI Solutions is not responsible for their content, privacy practices, or availability.",
      ]},
      { h: "Disclaimers and Limitation of Liability", p: [
        "The website is provided on an “as is” and “as available” basis. While we aim to keep all information accurate and up to date, we do not guarantee uninterrupted access, completeness, or fitness for a particular purpose.",
        "To the fullest extent permitted by law, V4 Data And AI Solutions will not be liable for indirect, incidental, or consequential losses arising from your use of this website.",
      ]},
      { h: "Governing Law and Changes", p: [
        "These Terms are governed by applicable laws in the jurisdiction from which V4 Data And AI Solutions operates. We may update these Terms from time to time, and continued use of the site after an update constitutes acceptance of the revised version.",
      ]},
    ]}
  />
);

export default LegalPage;
